import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { addressClient } from "@/core/api";

export interface State {
  addresses: {
    label: string;
    value: string | number;
    info: {
      [key: string]: any;
    };
  }[];
  status: "idle" | "loading" | "failed";
}

const initialState: State = {
  addresses: [],
  status: "idle",
};

// The function below is called a thunk and allows us to perform async logic. It
// can be dispatched like a regular action: `dispatch(incrementAsync(10))`. This
// will call the thunk with the `dispatch` function as the first argument. Async
// code can then be executed and other actions can be dispatched. Thunks are
// typically used to make async requests.
export const fetchedAddressByPostcode = createAsyncThunk(
  "address/fetchedAddressByPostcode",
  async (postcode: string) => {
    const response = await addressClient.getAddressByPostcode({ postcode });
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);

export const addressSlice = createSlice({
  name: "address",
  initialState,
  reducers: {
    setAddresses: (state, { payload }) => {
      state.addresses = payload.addresses;
    },
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(fetchedAddressByPostcode.pending, (state) => {
        state.status = "loading";
        state.addresses = [];
      })
      .addCase(fetchedAddressByPostcode.fulfilled, (state, action) => {
        state.status = "idle";
        const payload = action.payload as {
          data: [
            {
              street: string;
              postcode: string;
              line1: string;
              line2: string;
              name: string;
              town: string;
              city: string;
              country: string;
              county: string;
            }
          ];
        };
        const addresses = payload.data.map((address) => {
          const str = [
            address?.name,
            address?.line1,
            address?.line2,
            address?.street,
          ]
            .filter((item) => !!item)
            .join(", ");
          return { label: str, value: str, info: address };
        });
        state.addresses = addresses;
      });
  },
});

export const { setAddresses } = addressSlice.actions;

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state: RootState) => state.counter.value)`
// export const selectCount = (state: RootState) => state.counter.value;

export default addressSlice.reducer;
